import * as React from "react";
import { navigate } from "gatsby";
import { useAsyncTaskLoading, useSnackBarCard } from "@/utils/hooks";
import FormContext, { initFormStore } from "@/contexts/Form";
import form, { FormAddress, FormValues } from "@/utils/form";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import contract, {
  Feature,
  HIDDEN_COMPONENT_OPTIONS,
  isHiddenComponent,
  isReasonForSaleComponentHidden,
} from "@/utils/contract";
import { getTrafficSourceIdForSessionStorage } from "@/utils/trafficSource";
import AppStatus from "@/constants/AppStatus";
import PropertyType from "@/constants/PropertyType";
// FIXME: eslint fix かけても問題なくなったら object-curly-newline を修正する
// eslint-disable-next-line object-curly-newline
import apis, { ValuationRequest, UnitCondition, HouseCondition, LandCondition } from "@/apis/valuation";
import routings from "@/utils/routings";
import { APIError } from "@/utils/fetch";
import { isBlank } from "@/utils/is";
import { Inquiry } from "@/utils/valuation";
import { switchPrefectureCode } from "@/constants/Prefecture";
export type InjectProps = {
  values: FormValues;
  isValid: boolean;
  fetchValuation: () => void;
};

const createParameter: (
  customerId: string | null,
  contractId: number,
  values: FormValues,
  addressIds: FormAddress,
  isTrafficInfoAutoLinked?: boolean
) => ValuationRequest = (customerId, contractId, values, addressIds, isTrafficInfoAutoLinked) => {
  const inquiryParams = {
    customerId,
    contractId,
    lastName: values.lastName,
    firstName: values.firstName,
    lastNameKana: isBlank(values.lastNameKana) ? null : values.lastNameKana,
    firstNameKana: isBlank(values.firstNameKana) ? null : values.firstNameKana,
    tel: values.tel,
    contactTime: values.contactTime,
    canContactTel: values.canContactTel,
    mailAddress: values.mailAddress,
    canContactMail: values.canContactMail,
    postCode: isBlank(values.postCode) ? null : values.postCode,
    pref: isBlank(values.pref) ? null : values.pref,
    city: isBlank(values.city) ? null : values.city,
    town: isBlank(values.town) ? null : values.town,
    street: isBlank(values.street) ? null : values.street,
    building: values.building,
    saleReason: isBlank(values.saleReason) ? null : values.saleReason,
    salePeriod: isBlank(values.salePeriod) ? null : values.salePeriod,
    relation: isBlank(values.relation) ? null : values.relation,
    shopPrefId: isBlank(values.shopPrefId) ? null : switchPrefectureCode(values.shopPrefId),
    shopId: isBlank(values.shopId) ? null : values.shopId,
    trafficSourceId: getTrafficSourceIdForSessionStorage(),
    referralSourceCd: isBlank(values.referralSourceCd) ? null : values.referralSourceCd,
  };

  let conditionParams = {
    type: values.propertyType,
    pref: values.propertyPref,
    city: values.propertyCity,
    town: values.propertyTown,
    line: (isBlank(values.line) ? null : values.line) as string | null,
    station: isBlank(values.station) ? null : (values.station as string | null),
    bus: isBlank(values.bus) ? null : +values.bus,
    walk: isBlank(values.walk) ? null : +values.walk,
  };
  if (isTrafficInfoAutoLinked === true) {
    conditionParams.line = null;
    conditionParams.station = null;
    conditionParams.bus = null;
    conditionParams.walk = null;
    (conditionParams as UnitCondition & { street?: string }).street = values.propertyStreet;
  }
  if (PropertyType.MANSION === values.propertyType) {
    conditionParams = {
      ...conditionParams,
      name: values.propertyName,
      builtDate: `${values.builtDateYear}-${values.builtDateMonth}`,
      roomNumber: values.roomNumber,
      unitSize: isBlank(values.unitSize) ? null : +values.unitSize,
      story: isBlank(values.story) ? null : +values.story,
      floorPlan: values.floorPlan,
      balconyDirection: values.balconyDirection,
      prefId: addressIds.prefId,
      cityId: addressIds.cityId,
      townId: addressIds.townId,
      buildingName: values.buildingName,
      totalStory: isBlank(values.totalStory) ? null : +values.totalStory,
      structure: isBlank(values.structure) ? null : values.structure,
    } as UnitCondition & { street?: string };
  }
  if (PropertyType.HOUSE === values.propertyType) {
    conditionParams = {
      ...conditionParams,
      street: values.propertyStreet,
      builtDate: `${values.builtDateYear}-${values.builtDateMonth}`,
      totalStory: isBlank(values.totalStory) ? null : +values.totalStory,
      floorPlan: values.floorPlan,
      houseSize: isBlank(values.houseSize) ? null : +values.houseSize,
      landSize: isBlank(values.landSize) ? null : +values.landSize,
      structure: values.structure,
      undergroundStory: isBlank(values.undergroundStory) ? null : +values.undergroundStory,
      connecting: isBlank(values.connecting.status) ? null : values.connecting,
      prefId: addressIds.prefId,
      cityId: addressIds.cityId,
      townId: addressIds.townId,
    } as HouseCondition;
  }
  if (PropertyType.LAND === values.propertyType) {
    conditionParams = {
      ...conditionParams,
      street: values.propertyStreet,
      landSize: +values.landSize,
      connecting: isBlank(values.connecting.status) ? null : values.connecting,
      prefId: addressIds.prefId,
      cityId: addressIds.cityId,
      townId: addressIds.townId,
    } as LandCondition;
  }
  return {
    inquiry: inquiryParams,
    condition: conditionParams,
  } as ValuationRequest;
};

const withEffect = (WrappedComponent: React.ElementType<InjectProps>) => {
  return (props: unknown) => {
    const {
      state: { values, addressIds },
      isRequiredCustomer,
      isRequiredProperty,
      isRequiredPropertyDetails,
      isRequiredReason,
      isValidCustomer,
      isValidProperty,
      isValidPropertyDetails,
    } = React.useContext(FormContext) || initFormStore(form);

    const {
      state: { contractId, customerId, contractSearchId, featuresObj },
    } = React.useContext(ContractContext) || initContractStore(contract);

    const snackBarOpen = useSnackBarCard("alert");

    const { start, result, error } = useAsyncTaskLoading<Inquiry, [ValuationRequest]>(apis.valuationAsync);
    const isTrafficInfoAutoLinked = featuresObj[Feature.TRAFFIC_INFO_AUTO_LINK]?.enabled;

    const fetchValuationHandler = React.useCallback(() => {
      start(createParameter(customerId, contractId!, values, addressIds, isTrafficInfoAutoLinked));
    }, [values, customerId, contractId]);

    const notCheckAddress = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);

    const notCheckKana = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_KANA);

    const notCheckTel =
      isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_TEL) ||
      Boolean(featuresObj[Feature.TEL_OPTIONAL]);

    const notCheckPostCode = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_POST_CODE);

    const notCheckShop = Boolean(featuresObj[Feature.SHOP_SETTINGS]?.enabled);
    const notCheckReferralSource = Boolean(featuresObj[Feature.REFERRAL_SOURCE]?.enabled);

    const hiddenPurchaseIntentionComponent = isHiddenComponent(
      featuresObj,
      HIDDEN_COMPONENT_OPTIONS.PURCHASE_INTENTION
    );
    const hiddenReasonForSaleComponent = isReasonForSaleComponentHidden(featuresObj);
    const isConnectingRoadInfoAutoEnabled = featuresObj[Feature.CONNECTING_ROAD_INFO_AUTO]?.enabled;
    const isTotalStoryEnabled = !isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.TOTAL_STORY);
    const isStructureEnabled = !isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.STRUCTURE);

    const isValid =
      isRequiredProperty(isTrafficInfoAutoLinked, isTotalStoryEnabled, isStructureEnabled) &&
      isRequiredPropertyDetails(isConnectingRoadInfoAutoEnabled) &&
      isRequiredReason(hiddenPurchaseIntentionComponent, hiddenReasonForSaleComponent) &&
      isRequiredCustomer({
        kana: notCheckKana,
        tel: notCheckTel,
        address: notCheckAddress,
        postCode: notCheckPostCode,
        shopId: !notCheckShop,
        shopPrefId: !notCheckShop,
        referralSource: !notCheckReferralSource,
      }) &&
      isValidProperty(isTrafficInfoAutoLinked, isTotalStoryEnabled, isStructureEnabled) &&
      isValidPropertyDetails(isConnectingRoadInfoAutoEnabled) &&
      isValidCustomer();

    React.useEffect(() => {
      if (result) {
        const DIRECT_RESULT = Boolean(featuresObj[Feature.DIRECT_RESULT]);
        if (DIRECT_RESULT) {
          navigate(`${routings.estimation.location}?cr=${contractSearchId}&iq=${result.searchId}`, {
            state: { values },
          });
        } else {
          navigate(`${routings.complete.location}?cr=${contractSearchId}`);
        }
      }
      if (error) {
        const { type } = (error as unknown) as APIError;
        if (AppStatus.EXPIRED_DATE === type) {
          navigate(`${routings.expire.location}?cr=${contractSearchId}`);
        } else if (AppStatus.ASSESSMENT_LIMIT === type) {
          navigate(`${routings.exceed.location}?cr=${contractSearchId}`);
        } else {
          snackBarOpen("AI査定に失敗しました。恐れ入りますが再度お試しください。");
        }
      }
    }, [result, error]);

    return (
      <WrappedComponent values={values} fetchValuation={fetchValuationHandler} isValid={isValid} {...(props as any)} />
    );
  };
};

export default withEffect;
